import { Link } from "react-router-dom";
import { Logo } from "@/components/common/Logo";

/**
 * Every entry here goes somewhere real.
 *
 * This footer used to be plain list items styled to look like navigation --
 * twelve things that invited a click and did nothing. Items without a
 * destination are now simply not listed, which is the honest version: a link
 * to a page you have not written is worse than not offering it, because it
 * costs the reader a click to find that out.
 *
 * Careers and Customers are the two that went, since neither page exists.
 */

const CONTACT = process.env.REACT_APP_CONTACT_EMAIL || "hello@example.com";

const PRODUCT = [
  { label: "Post composer", to: "/#features" },
  { label: "Approval workflow", to: "/#features" },
  { label: "Scheduling calendar", to: "/#features" },
  { label: "Analytics", to: "/#features" },
];

const LEGAL = [
  { label: "Privacy", to: "/privacy" },
  { label: "Terms", to: "/terms" },
  { label: "Security", to: "/security" },
  { label: "GDPR", to: "/gdpr" },
];

const linkClass =
  "text-sm text-[#43535E] transition-colors hover:text-[#1F5FD8] hover:underline";

export default function MarketingFooter() {
  return (
    <footer className="mt-24 border-t border-[#E4E9EF] bg-[#F6F8FA]">
      <div className="mx-auto max-w-7xl px-6 py-12">
        <div className="grid gap-8 md:grid-cols-4">
          <div>
            <Logo />
            <p className="mt-3 max-w-xs text-sm text-[#43535E]">
              Think Bigger. Post Smarter. Grow Faster. Plan, approve, and publish social content across every network your team uses.
            </p>
          </div>

          <div>
            <div className="mb-3 text-xs font-semibold uppercase tracking-wider text-[#8A97A0]">Product</div>
            <ul className="space-y-2">
              {PRODUCT.map((item) => (
                <li key={item.label}>
                  <Link
                    to={item.to}
                    className={linkClass}
                    data-testid={`footer-product-${item.label.split(" ")[0].toLowerCase()}`}
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <div className="mb-3 text-xs font-semibold uppercase tracking-wider text-[#8A97A0]">Company</div>
            <ul className="space-y-2">
              <li>
                <Link to="/#pricing" className={linkClass} data-testid="footer-pricing">Pricing</Link>
              </li>
              <li>
                <Link to="/setup-guide" className={linkClass} data-testid="footer-setup-guide">Setup guide</Link>
              </li>
              <li>
                <a href={`mailto:${CONTACT}`} className={linkClass} data-testid="footer-contact">Contact</a>
              </li>
              <li>
                <Link to="/login" className={linkClass} data-testid="footer-signin">Sign in</Link>
              </li>
            </ul>
          </div>

          <div>
            <div className="mb-3 text-xs font-semibold uppercase tracking-wider text-[#8A97A0]">Legal</div>
            <ul className="space-y-2">
              {LEGAL.map((item) => (
                <li key={item.label}>
                  <Link to={item.to} className={linkClass} data-testid={`footer-${item.label.toLowerCase()}`}>
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-10 flex flex-col items-start justify-between gap-3 border-t border-[#E4E9EF] pt-6 text-xs text-[#8A97A0] md:flex-row md:items-center">
          <div>© {new Date().getFullYear()} SocialMind. All rights reserved.</div>
          <div>Made for teams that ship steady, quality content.</div>
        </div>
      </div>
    </footer>
  );
}
